// ---------------------------------------------------------------------------
// Prezio Onboard Widget — Parent Frame Bridge
// ---------------------------------------------------------------------------
// Sends lifecycle events to the parent window via postMessage when the
// widget is embedded inside an iframe.  This allows dashboard / host pages
// that embed the widget via <iframe> to listen for completion, cancellation
// and error events without needing direct JS callback access.
//
// Message shape:
//   { type: 'prezio:onboard', event: 'complete' | 'cancel' | 'error', ...payload }
// ---------------------------------------------------------------------------

/** Check at call-time whether the widget is inside an iframe. */
function isInIframe(): boolean {
  return typeof window !== 'undefined' && window !== window.parent;
}

/**
 * Post a structured message to the parent frame.
 *
 * - Only fires when the widget is embedded in an iframe.
 * - Silently swallows errors (e.g. cross-origin restrictions on some
 *   older browsers).
 * - Uses target origin `'*'` — callers must never include secrets.
 */
export function notifyParent(
  event: 'complete' | 'cancel' | 'error',
  payload?: Record<string, unknown>,
): void {
  if (!isInIframe()) return;

  try {
    window.parent.postMessage(
      { type: 'prezio:onboard', event, ...payload },
      '*',
    );
  } catch {
    /* silent — cross-origin or sandboxed frames may throw */
  }
}
